import { useEffect, useState } from 'react'

type OAuthPlatform = 'wechat' | 'alipay'

interface OAuthConfig {
  userAgentPattern: RegExp
  codeParamName: string
  buildAuthUrl: (appId: string, redirectUri: string) => string
}

const OAUTH_CONFIGS: Record<OAuthPlatform, OAuthConfig> = {
  wechat: {
    userAgentPattern: /MicroMessenger/i,
    codeParamName: 'code',
    buildAuthUrl: (appId, redirectUri) =>
      `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`,
  },
  alipay: {
    userAgentPattern: /AlipayClient/i,
    codeParamName: 'auth_code',
    buildAuthUrl: (appId, redirectUri) =>
      `https://openauth.alipay.com/oauth2/publicAppAuthorize.htm?app_id=${appId}&redirect_uri=${redirectUri}&scope=auth_base`,
  },
}

interface UseOAuthOptions {
  platform: OAuthPlatform
  appId?: string
}

interface UseOAuthResult {
  authCode: string | null
  authLoading: boolean
}

export const useOAuth = ({ platform, appId }: UseOAuthOptions): UseOAuthResult => {
  const [authCode, setAuthCode] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!appId || appId === '') {
      setAuthLoading(false)
      return
    }

    const config = OAUTH_CONFIGS[platform]
    const isTargetPlatform = config.userAgentPattern.test(navigator.userAgent)

    if (!isTargetPlatform) {
      setAuthLoading(false)
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get(config.codeParamName)

    if (!code) {
      const redirectUri = encodeURIComponent(window.location.href)
      window.location.href = config.buildAuthUrl(appId, redirectUri)
    } else {
      setAuthCode(code)
      setAuthLoading(false)
    }
  }, [platform, appId])

  return { authCode, authLoading }
}

export type { OAuthPlatform, UseOAuthOptions, UseOAuthResult }
